import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCountdownOptions {
  /** Initial countdown value in seconds */
  initialValue: number;
  /** Callback when countdown reaches 0 */
  onComplete?: () => void;
  /** Whether the countdown should start immediately */
  autoStart?: boolean;
  /** Whether the countdown should loop back to initial value when it reaches 0 */
  loop?: boolean;
}

export interface UseCountdownReturn {
  /** Current countdown value */
  count: number;
  /** Whether the countdown is currently running */
  isRunning: boolean;
  /** Whether the countdown has completed (reached 0) */
  isCompleted: boolean;
  /** Start or resume the countdown */
  start: () => void;
  /** Pause the countdown */
  pause: () => void;
  /** Reset countdown to initial value */
  reset: () => void;
  /** Reset and start the countdown */
  restart: () => void;
}

export function useCountdown({
  initialValue,
  onComplete,
  autoStart = false,
  loop = false,
}: UseCountdownOptions): UseCountdownReturn {
  const [count, setCount] = useState(initialValue);
  const [isRunning, setIsRunning] = useState(autoStart);
  const [isCompleted, setIsCompleted] = useState(false);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const start = useCallback(() => {
    if (count > 0) {
      setIsRunning(true);
      setIsCompleted(false);
    }
  }, [count]);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setCount(initialValue);
    setIsRunning(false);
    setIsCompleted(false);
  }, [initialValue]);

  const restart = useCallback(() => {
    setCount(initialValue);
    setIsRunning(true);
    setIsCompleted(false);
  }, [initialValue]);

  useEffect(() => {
    if (!isRunning || count <= 0) {
      return;
    }

    const timerId = setTimeout(() => {
      const nextCount = count - 1;
      setCount(nextCount);

      if (nextCount <= 0) {
        setIsRunning(false);
        setIsCompleted(true);
        onCompleteRef.current?.();

        if (loop) {
          setTimeout(() => {
            setCount(initialValue);
            setIsRunning(true);
            setIsCompleted(false);
          }, 0);
        }
      }
    }, 1000);

    return () => clearTimeout(timerId);
  }, [count, isRunning, loop, initialValue]);

  return {
    count,
    isRunning,
    isCompleted,
    start,
    pause,
    reset,
    restart,
  };
}
